import { z } from "zod";
import { Form } from "@/app/global-components/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import LoginFieldView from "./login-field-view";
import LoginSubmitView from "./login-submit-view";
import { useNavigate } from "react-router-dom";
import { storeData } from "@/lib/utils";
import { useLocale } from "@/middleware";
import { useTranslate } from "@/locales";
import { routes } from "@/consts";
import useLogin from "@/hooks/use-login";
import { showToast } from "@/app/global-components/toaster";
import { useEffect, useRef, useMemo, useCallback } from "react";
import { Box } from "@mui/material";
import CloudflareTurnstile from "@/components/CloudflareTurnstile";
import { getTurnstileSiteKey } from "@/config/cloudflare";

const LoginFormView = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const navigate = useNavigate();
  const captchaRef = useRef<any>(null);
  const {
    login,
    isLoading,
    error,
    showCaptcha,
    captchaToken,
    handleCaptchaVerify,
    handleCaptchaError,
    handleCaptchaExpire,
    resetCaptcha,
  } = useLogin();

  useEffect(() => {
    if (error) {
      showToast(String(error), "error");
    }
  }, [error]);

  const LoginSchema = z.object({
    email: z
      .string()
      .email({ message: t("authentication.validation.invalidEmail") }),
    password: z.string().min(8, {
      message: t("authentication.changePassword.validation.passwordLength"),
    }),
  });

  const form = useForm<z.infer<typeof LoginSchema>>({
    resolver: zodResolver(LoginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const handleCaptchaVerification = useCallback(
    (token: string) => {
      handleCaptchaVerify(token);
      // Don't reset immediately, let the login process handle it
      // The captcha will be reset on successful login or when a new error occurs
    },
    [handleCaptchaVerify]
  );

  const handleCaptchaErrorCallback = useCallback(() => {
    handleCaptchaError();
    // Reset the captcha widget on error after a delay
    setTimeout(() => {
      if (captchaRef.current) {
        captchaRef.current.reset();
      }
    }, 1000);
  }, [handleCaptchaError]);

  const handleCaptchaExpireCallback = useCallback(() => {
    handleCaptchaExpire();
    // Reset the captcha widget on expire after a delay
    setTimeout(() => {
      if (captchaRef.current) {
        captchaRef.current.reset();
      }
    }, 1000);
  }, [handleCaptchaExpire]);

  // Memoize the captcha props to prevent unnecessary re-renders
  const captchaProps = useMemo(
    () => ({
      siteKey: getTurnstileSiteKey(),
      onVerify: handleCaptchaVerification,
      onError: handleCaptchaErrorCallback,
      onExpire: handleCaptchaExpireCallback,
      theme: "light" as const,
      language: locale,
    }),
    [
      handleCaptchaVerification,
      handleCaptchaErrorCallback,
      handleCaptchaExpireCallback,
      locale,
    ]
  );

  // Debug logs to track re-renders
  useEffect(() => {
    console.log(
      "LoginFormView re-render - showCaptcha:",
      showCaptcha,
      "captchaToken:",
      !!captchaToken
    );
  });

  const onSubmit = async (values: z.infer<typeof LoginSchema>) => {
    try {
      // If captcha is required and we have a token, include it
      const response = await login(values, captchaToken || undefined);
      if (response.isSuccess) {
        storeData("tokens", response.data.tokens);
        storeData("userInfo", response.data.user);
        // Reset captcha on successful login
        resetCaptcha();
        navigate(routes.home);
      } else {
        showToast(response.message || t("alerts.error.loginError"), "error");
      }
    } catch (err) {
      // Don't show error toast here as it's already handled in the login hook
      console.log("Login error:", err);
      // The error will be displayed through the error state in the component

      // Don't reset captcha on error - let the login hook handle it
      // The captcha should stay visible if it was triggered
    }
  };

  return (
    <Form {...form}>
      <Box
        component="form"
        onSubmit={form.handleSubmit(onSubmit)}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <LoginFieldView
          name="email"
          label={t("profile.fields.email")}
          placeholder={t("profile.fields.email")}
          inputType="email"
          formControl={form.control}
        />
        <LoginFieldView
          name="password"
          label={t("authentication.fields.password")}
          placeholder={t("authentication.fields.password")}
          description={t("authentication.validation.passwordDesc")}
          inputType="password"
          formControl={form.control}
        />

        {/* Show Cloudflare Turnstile when captcha is required */}
        {showCaptcha && (
          <CloudflareTurnstile ref={captchaRef} {...captchaProps} />
        )}

        <LoginSubmitView isLoading={isLoading} />
        {error && (
          <Box
            sx={{
              color: "error.main",
              fontSize: "0.875rem",
              textAlign: "center",
              mt: 1,
            }}
          >
            {error}
          </Box>
        )}
      </Box>
    </Form>
  );
};

export default LoginFormView;
